import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { photoFileUrl } from "@/lib/gallery";
import { fmtDate, formatApiError } from "@/lib/utils";
import Lightbox, { useFavourites } from "@/components/Lightbox";
import GuestOnboardingFlow from "@/components/GuestOnboardingFlow";
import GuestsPanel from "@/components/GuestsPanel";
import FaceOverlay from "@/components/FaceOverlay";
import MobileActionBar from "@/components/MobileActionBar";
import KBRJFooter from "@/components/KBRJFooter";
import StudioLogo from "@/components/StudioLogo";
import { useBranding } from "@/lib/branding";
import {
  Folder, FolderOpen, ArrowLeft, Camera, Sparkles, Loader2, X, ImageOff,
  Search, CheckCircle2, AlertCircle, Download, Share2, Heart, Gem, QrCode,
  Users, Eye, EyeOff,
} from "lucide-react";

/**
 * Public guest-facing gallery. No auth required.
 * Route: /g/:eventId (registered outside the Protected Layout).
 *
 * Guests can:
 *   1. Browse any folder / sub-folder that is marked is_public.
 *   2. Click "Find My Photos" → provide WhatsApp + selfie → we run face
 *      matching on the server and show only photos where their face appears.
 */

const ALLOWED_SELFIE_MIME = /image\/(jpeg|jpg|png|webp|heic|heif)/i;

async function fetchPublic(eventId) {
  const { data } = await axios.get(`${API}/public/gallery/${eventId}`);
  return data;
}

export default function PublicGallery() {
  const { eventId } = useParams();

  const navigate = useNavigate();
  const branding = useBranding();

  const [mainEvent, setMainEvent] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [selectedNode, setSelectedNode] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadErr, setLoadErr] = useState("");

  // Face-match state
  const [gateOpen, setGateOpen] = useState(false);
  const [matchState, setMatchState] = useState(null); // { matchIds:Set, count, unprocessed, lookupId }
  const [lightboxIdx, setLightboxIdx] = useState(null); // number | null
  const [onlyMine, setOnlyMine] = useState(true);
  // iter90 — Wedfaces UI state
  const [guestsOpen, setGuestsOpen] = useState(false);
  const [showFaces, setShowFaces] = useState(false);
  const [faceMap, setFaceMap] = useState(null); // { photoId: {bboxes, img_w, img_h} }
  const [intersectionLabel, setIntersectionLabel] = useState("");

  // Favourites — per-event, per-browser. Persisted to the studio via
  // POST /api/public/gallery/{event_id}/manage-selection whenever the guest
  // opens/closes their favourites tray so Rakesh sees the shortlist even if
  // they never send it manually.
  const favs = useFavourites(`kahani_favs_${eventId}`);
  const [savingFavs, setSavingFavs] = useState(false);
  const [favsSavedAt, setFavsSavedAt] = useState(0);
  const [favsError, setFavsError] = useState("");
  const [showQr, setShowQr] = useState(false);
  const [showDownload, setShowDownload] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadErr("");
    try {
      const data = await fetchPublic(eventId);
      const isSub = !!(data.parent_id || "").trim();
      if (isSub && data.parent) {
        const mainData = await fetchPublic(data.parent.id);
        setMainEvent(mainData);
      } else {
        setMainEvent(data);
      }
      setSelectedId(data.id);
      setSelectedNode(data);
    } catch (e) {
      setLoadErr(formatApiError(e.response?.data?.detail) || "This gallery isn't available.");
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => { load(); }, [load]);

  const selectNode = async (id) => {
    if (id === selectedId) return;
    setSelectedId(id);
    try {
      const data = await fetchPublic(id);
      setSelectedNode(data);
    } catch (e) {
      setLoadErr(formatApiError(e.response?.data?.detail) || "Couldn't load that folder.");
    }
  };

  const sendFavourites = async () => {
    if (!mainEvent) return;
    setSavingFavs(true);
    setFavsError("");
    try {
      const payload = {
        action: "set",
        photo_ids: Array.from(favs.ids),
        whatsapp: matchState?.whatsapp || "",
        lookup_id: matchState?.lookupId || "",
        name: matchState?.guestName || "",
      };
      if (!payload.lookup_id && !payload.whatsapp) {
        // Anonymous browsers still need a stable session — synth one lazily.
        let sid = localStorage.getItem("kahani_anon_session");
        if (!sid) {
          sid = `anon-${Math.random().toString(36).slice(2, 10)}-${Date.now().toString(36)}`;
          localStorage.setItem("kahani_anon_session", sid);
        }
        payload.lookup_id = sid;
      }
      await axios.post(`${API}/public/gallery/${mainEvent.id}/manage-selection`, payload);
      setFavsSavedAt(Date.now());
    } catch (e) {
      setFavsError(formatApiError(e.response?.data?.detail) || "Couldn't save — try again.");
    } finally {
      setSavingFavs(false);
    }
  };

  if (loading) {
    // iter147 — Skeleton loading state so the gallery frame renders
    // immediately (no CLS as photos hydrate).
    return (
      <div className="min-h-screen bg-[#f5efe4]" data-testid="public-gallery-loading" aria-busy="true">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 space-y-6">
          <div className="flex items-center gap-4">
            <div className="kb-skeleton w-10 h-10 rounded-full" />
            <div className="flex-1 space-y-2">
              <div className="kb-skeleton h-3 w-32" />
              <div className="kb-skeleton h-6 w-64 max-w-full" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {Array.from({ length: 12 }).map((_, i) => (
              <div key={i} className="kb-skeleton aspect-[4/3] rounded-lg" />
            ))}
          </div>
        </div>
        <span className="sr-only">Loading your gallery…</span>
      </div>
    );
  }
  if (loadErr || !mainEvent || !selectedNode) {
    return (
      <div className="min-h-screen bg-[#f5efe4] flex items-center justify-center px-6" data-testid="public-gallery-error">
        <div className="max-w-md w-full text-center bg-white border border-stone-200 rounded-2xl p-8 shadow-sm kb-fade-in">
          <div className="mx-auto w-12 h-12 rounded-full bg-rose-50 border border-rose-200 flex items-center justify-center mb-4">
            <ImageOff className="w-5 h-5 text-rose-600" strokeWidth={1.8} aria-hidden="true" />
          </div>
          <div className="text-[10px] tracking-[0.35em] uppercase text-stone-500 mb-2 font-semibold">Kahani Studio</div>
          <div className="font-heading text-2xl font-semibold mb-2 text-stone-900">Gallery unavailable</div>
          <p className="text-stone-500 text-sm leading-relaxed">{loadErr || "Please check the link and try again."}</p>
        </div>
      </div>
    );
  }

  const isMainSelected = selectedId === mainEvent.id;
  const photos = selectedNode.photos || [];
  const subEvents = mainEvent.sub_events || [];
  const matchSet = matchState?.matchIds;

  const visiblePhotos = matchSet && onlyMine ? photos.filter((p) => matchSet.has(p.id)) : photos;

  return (
    <div className="min-h-screen bg-[#f5efe4]" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>
      {/* iter197 — Editorial hero, cream + tan · matches PublicProposal aesthetic */}
      <header className="relative border-b border-[#e7d5b4]/60 bg-gradient-to-b from-[#faf5eb] to-[#f5efe4] overflow-hidden">
        {/* iter198 — Studio logo brand mark (falls back to serif wordmark) */}
        <div className="max-w-4xl mx-auto px-5 pt-8 md:pt-12 flex justify-center" data-testid="gallery-hero-logo">
          <StudioLogo size={56} className="mx-auto max-h-[56px] opacity-95" data-testid="public-gallery-studio-logo" />
        </div>
        {/* Decorative rule row */}
        <div className="max-w-4xl mx-auto px-5 pt-6 md:pt-8">
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-[#e7d5b4]" />
            <div className="text-[10px] tracking-[0.4em] uppercase text-[#8c7a63] font-medium whitespace-nowrap">
              Wedding Gallery
            </div>
            <div className="h-px flex-1 bg-[#e7d5b4]" />
          </div>
        </div>

        {/* Couple mark */}
        <div className="max-w-4xl mx-auto px-5 py-10 md:py-14 text-center">
          <h1
            className="font-serif text-[#3b2f24] text-4xl md:text-6xl leading-[1.05] tracking-tight"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {(mainEvent.couple_name || mainEvent.name || "").toUpperCase()}
          </h1>
          {mainEvent.couple_name && mainEvent.name !== mainEvent.couple_name && (
            <div className="mt-3 text-[11px] uppercase tracking-[0.3em] text-[#8c7a63]">
              {mainEvent.name}
            </div>
          )}
          <div className="mt-5 text-[11px] uppercase tracking-[0.35em] text-[#8c7a63] flex items-center justify-center gap-3 flex-wrap">
            {mainEvent.event_date && <span>{fmtDate(mainEvent.event_date)}</span>}
            {mainEvent.event_date && <span className="text-[#c9b48e]">·</span>}
            <span>{mainEvent.photo_count} photographs</span>
          </div>

          {/* Primary + secondary actions */}
          <div className="mt-9 flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3">
            <button
              onClick={() => setGateOpen(true)}
              data-testid="open-find-my-photos"
              className="group inline-flex items-center justify-center gap-2.5 bg-[#3b2f24] text-[#f5efe4] hover:bg-[#2a2119] text-[11px] tracking-[0.3em] uppercase font-medium px-8 py-4 rounded-full shadow-[0_8px_28px_rgba(59,47,36,0.22)] transition"
            >
              <Sparkles className="w-3.5 h-3.5" strokeWidth={1.6} />
              {matchState ? "Update my selfie" : "Find my photos"}
            </button>
          </div>
          <div className="mt-4 flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-[10px] uppercase tracking-[0.28em] text-[#8c7a63]">
            <button onClick={() => setShowQr(true)} data-testid="public-show-qr-btn"
                    className="inline-flex items-center gap-1.5 hover:text-[#3b2f24] transition">
              <Share2 className="w-3 h-3" strokeWidth={1.8} /> Share
            </button>
            <span className="text-[#c9b48e]">·</span>
            <button onClick={() => setShowDownload(true)} data-testid="public-download-zip-btn"
                    className="inline-flex items-center gap-1.5 hover:text-[#3b2f24] transition">
              <Download className="w-3 h-3" strokeWidth={1.8} /> Download album
            </button>
            <span className="text-[#c9b48e]">·</span>
            <button onClick={() => setGuestsOpen((v) => !v)} data-testid="public-toggle-guests"
                    className="inline-flex items-center gap-1.5 hover:text-[#3b2f24] transition">
              <Users className="w-3 h-3" strokeWidth={1.8} /> {guestsOpen ? "Hide guests" : "Guests"}
            </button>
            <span className="text-[#c9b48e]">·</span>
            <button
              onClick={async () => {
                const next = !showFaces;
                setShowFaces(next);
                if (next && !faceMap) {
                  try {
                    const { data } = await axios.get(`${API}/public/gallery/${mainEvent.id}/photo-faces`);
                    setFaceMap(data.photos || {});
                  } catch (err) {
                    setFaceMap({});
                  }
                }
              }}
              data-testid="public-toggle-show-faces"
              className="inline-flex items-center gap-1.5 hover:text-[#3b2f24] transition"
            >
              {showFaces ? <EyeOff className="w-3 h-3" strokeWidth={1.8} /> : <Eye className="w-3 h-3" strokeWidth={1.8} />}
              {showFaces ? "Hide faces" : "Show faces"}
            </button>
          </div>
        </div>
        {/* Closing rule */}
        <div className="max-w-4xl mx-auto px-5 pb-6">
          <div className="h-px bg-[#e7d5b4]" />
        </div>
      </header>

      {/* iter90 — Guests panel drops in below the hero when toggled. */}
      {guestsOpen && (
        <div className="max-w-6xl mx-auto px-5 mt-4">
          <GuestsPanel
            eventId={mainEvent.id}
            onClose={() => setGuestsOpen(false)}
            onIntersection={(photoIds, label, guestIds) => {
              setMatchState({
                matchIds: new Set(photoIds),
                count: photoIds.length,
                unprocessed: 0,
                lookupId: "",
                upsellLink: "",
              });
              setIntersectionLabel(label);
              setOnlyMine(true);
              setGuestsOpen(false);
            }}
          />
        </div>
      )}

      {/* Match summary bar */}
      {matchState && (
        <div className="max-w-6xl mx-auto px-5 mt-6">
          <div className="flex flex-wrap items-center gap-3 bg-white/80 backdrop-blur-sm border border-[#e7d5b4] text-[#3b2f24] px-5 py-3.5 rounded-2xl text-sm shadow-[0_6px_24px_rgba(140,90,50,0.08)]" data-testid="match-summary">
            {intersectionLabel ? <Users className="w-4 h-4 shrink-0 text-[#8c7a63]" strokeWidth={1.8} /> : <Heart className="w-4 h-4 shrink-0 text-[#8c7a63]" strokeWidth={1.8} />}
            <span>
              {intersectionLabel ? (
                <>Showing <strong className="font-serif italic">{matchState.count}</strong> photograph{matchState.count === 1 ? "" : "s"} with <strong>{intersectionLabel}</strong>.</>
              ) : (
                <>Your photos are ready — <strong className="font-serif italic">{matchState.count}</strong> photograph{matchState.count === 1 ? "" : "s"} featuring you.
                {matchState.unprocessed > 0 && <span className="text-[#8c7a63]"> ({matchState.unprocessed} still processing.)</span>}</>
              )}
            </span>
            {matchState.count > 0 && (
              <button
                type="button"
                onClick={() => {
                  const ref = matchState.lookupId ? `?ref=${matchState.lookupId}` : "";
                  navigate(`/g/${mainEvent.id}/originals${ref}`);
                }}
                className="ml-auto inline-flex items-center gap-1.5 bg-[#3b2f24] hover:bg-[#2a2119] text-[#f5efe4] text-[10px] uppercase tracking-[0.24em] font-medium px-4 py-2 rounded-full transition"
                data-testid="get-full-album-cta"
              >
                <Gem className="w-3 h-3" strokeWidth={1.8} /> Get the full album
              </button>
            )}
            <label className={`${matchState.count > 0 ? "" : "ml-auto "}text-[10px] uppercase tracking-[0.24em] inline-flex items-center gap-2 cursor-pointer select-none text-[#8c7a63]`}>
              <input type="checkbox" checked={onlyMine} onChange={(e) => setOnlyMine(e.target.checked)} className="accent-[#3b2f24]" data-testid="only-mine-toggle" />
              Only me
            </label>
            <button className="text-[10px] uppercase tracking-[0.24em] text-[#8c7a63] hover:text-[#3b2f24]" onClick={() => { setMatchState(null); setIntersectionLabel(""); }} data-testid="clear-match">Clear</button>
          </div>
        </div>
      )}

      {/* Body */}
      <main className="max-w-6xl mx-auto px-5 py-8 md:py-10 grid grid-cols-1 md:grid-cols-[240px_1fr] gap-6 md:gap-8">
        {/* iter197 — Editorial "chapters" nav */}
        <aside className="bg-white/60 backdrop-blur-sm border border-[#e7d5b4] rounded-2xl p-4 h-fit shadow-[0_6px_24px_rgba(140,90,50,0.06)]">
          <div className="text-[10px] uppercase tracking-[0.32em] text-[#8c7a63] font-medium px-1 pb-3 border-b border-[#e7d5b4]/60">
            Album chapters
          </div>
          <div className="mt-3 space-y-0.5">
            <NodeButton
              node={mainEvent}
              selected={isMainSelected}
              onClick={() => selectNode(mainEvent.id)}
              matchCount={matchSet ? [...(matchSet)].filter(() => true).length : null /* not per-node; overall */}
              level="main"
              chapterNumber={null}
            />
            {subEvents.length > 0 && (
              <div className="mt-1 space-y-0.5">
                {subEvents.map((s, idx) => (
                  <NodeButton
                    key={s.id}
                    node={s}
                    selected={selectedId === s.id}
                    onClick={() => selectNode(s.id)}
                    level="sub"
                    chapterNumber={idx + 1}
                  />
                ))}
              </div>
            )}
          </div>
          {!isMainSelected && (
            <button
              onClick={() => selectNode(mainEvent.id)}
              className="mt-4 w-full text-[10px] uppercase tracking-[0.24em] text-[#8c7a63] hover:text-[#3b2f24] inline-flex items-center gap-1.5 px-2 py-1.5 transition"
            >
              <ArrowLeft className="w-3 h-3" strokeWidth={1.8} /> Back to main album
            </button>
          )}
        </aside>

        {/* Photo grid */}
        <section>
          <div className="flex items-start justify-between mb-5 gap-4">
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#8c7a63] font-medium">
                {matchState && !intersectionLabel && onlyMine
                  ? "Your Photos"
                  : isMainSelected ? "The Collection" : "Chapter"}
              </div>
              <h2 className="font-serif text-[#3b2f24] text-2xl md:text-3xl mt-1"
                  style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                {matchState && !intersectionLabel && onlyMine
                  ? (mainEvent.couple_name || mainEvent.name)
                  : selectedNode.name}
              </h2>
              {matchState && !intersectionLabel && onlyMine && (
                <div className="text-[13px] italic text-[#8c7a63] mt-1.5 font-serif" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
                  Moments where you were part of the story
                </div>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-[0.24em] text-[#8c7a63] shrink-0 pt-2">
              {visiblePhotos.length} <span className="text-[#c9b48e]">/ {photos.length}</span>
            </div>
          </div>

          {visiblePhotos.length === 0 ? (
            <div className="card p-10 text-center bg-white kb-fade-in" role="status" data-testid="public-gallery-empty">
              <div className="mx-auto w-12 h-12 rounded-full bg-stone-50 border border-stone-200 flex items-center justify-center mb-3">
                <ImageOff className="w-5 h-5 text-stone-400" strokeWidth={1.6} aria-hidden="true" />
              </div>
              <div className="font-heading text-lg font-semibold text-stone-800 mb-1">
                {matchSet && onlyMine ? "No matches yet" : "No photos here yet"}
              </div>
              <p className="text-sm text-stone-500 max-w-sm mx-auto leading-relaxed">
                {matchSet && onlyMine
                  ? "Try another section or update your selfie — face matching improves as we index more images."
                  : "Photos will appear here once the studio finishes editing."}
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
              {visiblePhotos.map((p, tileIdx) => {
                const isMatch = matchSet?.has(p.id);
                const isFav = favs.ids.has(p.id);
                return (
                  <div
                    key={p.id}
                    className={`relative aspect-square group card overflow-hidden bg-stone-100 ${isMatch ? "ring-2 ring-rose-500" : ""}`}
                    data-testid={`public-photo-${p.id}`}
                  >
                    <button
                      type="button"
                      onClick={() => setLightboxIdx(tileIdx)}
                      className="absolute inset-0 focus:outline-none focus:ring-2 focus:ring-rose-500"
                      aria-label={`Open ${p.filename}`}
                    >
                      <img src={photoFileUrl(p.id)} alt={p.filename} loading="lazy" className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform" />
                    </button>
                    {/* iter90 — Face bbox pointers when Show Faces is on */}
                    {showFaces && faceMap && faceMap[p.id] && (
                      <FaceOverlay
                        bboxes={faceMap[p.id].bboxes}
                        imgW={faceMap[p.id].img_w}
                        imgH={faceMap[p.id].img_h}
                      />
                    )}
                    {isMatch && (
                      <div className="absolute top-1.5 left-1.5 bg-rose-600 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full inline-flex items-center gap-1 pointer-events-none">
                        <Heart className="w-2.5 h-2.5 fill-current" strokeWidth={0} /> You
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); favs.toggle(p.id); }}
                      title={isFav ? "Remove from favourites" : "Add to favourites"}
                      className={`absolute top-1.5 right-1.5 w-7 h-7 rounded-full flex items-center justify-center backdrop-blur-sm transition-colors ${
                        isFav
                          ? "bg-rose-600 text-white"
                          : "bg-black/40 text-white/70 hover:bg-black/60 hover:text-white opacity-0 group-hover:opacity-100"
                      }`}
                      data-testid={`favourite-toggle-${p.id}`}
                    >
                      <Heart className="w-3.5 h-3.5" strokeWidth={1.8} fill={isFav ? "currentColor" : "none"} />
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </section>
      </main>

      <footer className="border-t border-[#e7d5b4]/60 mt-8 bg-gradient-to-b from-[#f5efe4] to-[#faf5eb]">
        <div className="max-w-3xl mx-auto px-5 py-14 text-center">
          {/* iter198 — Studio logo as brand signature */}
          <div className="mb-6 flex justify-center">
            <StudioLogo size={72} className="max-h-[72px]" data-testid="gallery-footer-logo" showFallbackText={false} />
          </div>
          <div className="flex items-center gap-4 justify-center mb-6">
            <div className="h-px w-16 bg-[#e7d5b4]" />
            <span className="text-[9px] tracking-[0.4em] uppercase text-[#8c7a63]">Signature</span>
            <div className="h-px w-16 bg-[#e7d5b4]" />
          </div>
          <div
            className="font-serif text-[#3b2f24] text-2xl md:text-3xl mb-2"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {branding?.studio_name || "Kahani by Rakesh Janardan"}
          </div>
          <div
            className="text-[13px] italic text-[#7a6853] mb-5"
            style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
          >
            {branding?.tagline || "Weddings, portraits & the moments between"}
          </div>
          <div className="text-[10px] uppercase tracking-[0.35em] text-[#8c7a63] mb-4">
            {branding?.city || "Mumbai"} · {branding?.country || "India"}
          </div>
          {(branding?.website || branding?.instagram || branding?.contact_email) && (
            <div className="flex items-center justify-center gap-x-4 gap-y-1 flex-wrap text-[10px] uppercase tracking-[0.28em] text-[#8c7a63] pt-4 border-t border-[#e7d5b4]/40 mt-6">
              {branding?.website && (
                <a href={branding.website} target="_blank" rel="noreferrer" className="hover:text-[#3b2f24] transition" data-testid="gallery-footer-website">
                  Website
                </a>
              )}
              {branding?.instagram && (
                <>
                  <span className="text-[#c9b48e]">·</span>
                  <a href={branding.instagram.startsWith("http") ? branding.instagram : `https://instagram.com/${branding.instagram.replace(/^@/, "")}`}
                     target="_blank" rel="noreferrer" className="hover:text-[#3b2f24] transition" data-testid="gallery-footer-instagram">
                    Instagram
                  </a>
                </>
              )}
              {branding?.contact_email && (
                <>
                  <span className="text-[#c9b48e]">·</span>
                  <a href={`mailto:${branding.contact_email}`} className="hover:text-[#3b2f24] transition" data-testid="gallery-footer-email">
                    Contact
                  </a>
                </>
              )}
            </div>
          )}
          <div className="text-[9px] uppercase tracking-[0.3em] text-[#a89478] mt-6">
            © {new Date().getFullYear()} {branding?.studio_name || "Kahani by Rakesh Janardan"}
          </div>
        </div>
      </footer>

      {/* Floating favourites tray — appears when the guest has any favourites */}
      {favs.ids.size > 0 && (
        <div className="fixed inset-x-0 bottom-0 z-40 px-4 pb-4 pointer-events-none">
          <div className="pointer-events-auto max-w-2xl mx-auto flex items-center gap-3 bg-white shadow-2xl shadow-rose-900/20 border border-rose-100 rounded-full pl-4 pr-2 py-2 text-sm" data-testid="favourites-tray">
            <Heart className="w-4 h-4 text-rose-600 fill-rose-500 shrink-0" strokeWidth={0} />
            <span className="font-semibold text-stone-800">
              <span data-testid="favourites-count">{favs.ids.size}</span> favourite{favs.ids.size === 1 ? "" : "s"}
            </span>
            <span className="hidden sm:inline text-xs text-stone-400 truncate">
              {savingFavs ? "saving…" : favsError ? favsError : favsSavedAt ? "shared with studio" : "tap Send to share with the studio"}
            </span>
            <button
              type="button"
              onClick={() => favs.setAll([])}
              className="ml-auto text-xs text-stone-500 hover:text-stone-800 px-2 py-1"
              data-testid="favourites-clear"
            >
              Clear
            </button>
            <button
              type="button"
              onClick={sendFavourites}
              disabled={savingFavs}
              className="text-xs font-semibold bg-rose-600 hover:bg-rose-700 text-white px-3 py-1.5 rounded-full inline-flex items-center gap-1.5 disabled:opacity-60"
              data-testid="favourites-send"
            >
              {savingFavs ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" strokeWidth={2} />}
              {favsSavedAt ? "Update" : "Send my favourites"}
            </button>
          </div>
        </div>
      )}

      {/* Premium lightbox */}
      {lightboxIdx != null && visiblePhotos[lightboxIdx] && (
        <Lightbox
          photos={visiblePhotos}
          startIndex={lightboxIdx}
          onClose={() => setLightboxIdx(null)}
          getImageUrl={(p) => photoFileUrl(p.id)}
          getDownloadUrl={(p) => `${API}/public/gallery/photos/${p.id}/download`}
          getFilename={(p) => p.filename || "photo.jpg"}
          selectedIds={favs.ids}
          onToggleSelect={(id) => favs.toggle(id)}
          onDeleteSelected={async (idsToRemove) => favs.clear(idsToRemove)}
          deleteLabel="Remove from favourites"
          deleteVerb="remove"
          destructive={false}
          showShare
          shareUrl={typeof window !== "undefined" ? window.location.href.split("?")[0] : ""}
          showDownload
        />
      )}

      {/* Guest onboarding wizard: name → camera → quality → results */}
      {gateOpen && (
        <GuestOnboardingFlow
          eventId={mainEvent.id}
          coupleName={mainEvent.couple_name || mainEvent.name}
          onClose={() => setGateOpen(false)}
          onDone={(res) => {
            setMatchState({
              matchIds: new Set(res.matches.map((m) => m.id)),
              count: res.match_count,
              unprocessed: res.unprocessed_photos,
              lookupId: res.lookup_id || "",
              upsellLink: res.upsell_link || "",
            });
            setGateOpen(false);
            setOnlyMine(true);
          }}
        />
      )}

      {/* QR Modal — anyone with the link can grab the QR to share further */}
      {showQr && mainEvent && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={() => setShowQr(false)} data-testid="public-qr-modal">
          <div className="bg-white rounded-xl shadow-2xl max-w-sm w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setShowQr(false)}
              className="absolute top-2 right-2 text-stone-500 hover:text-stone-900 p-1"
              data-testid="public-qr-close"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
            <div className="text-[11px] uppercase tracking-[0.25em] text-rose-700 font-semibold">Kahani gallery</div>
            <h3 className="font-heading text-xl font-semibold text-stone-900 mt-1">{mainEvent.couple_name || mainEvent.name}</h3>
            <p className="text-xs text-stone-500 mt-1">Scan or share this QR to open the album on any phone.</p>
            <div className="mt-4 rounded-lg border border-stone-200 bg-stone-50 p-3 flex items-center justify-center">
              <img
                src={`${API}/public/gallery/${mainEvent.id}/qr`}
                alt={`QR code for ${mainEvent.name}`}
                className="w-64 h-64 object-contain"
                data-testid="public-qr-image"
              />
            </div>
            <div className="mt-4 flex justify-end">
              <a
                className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 text-white text-xs font-semibold px-3 py-1.5 rounded-full"
                href={`${API}/public/gallery/${mainEvent.id}/qr`}
                download={`kahani-qr-${(mainEvent.couple_name || mainEvent.name || "gallery").replace(/[^A-Za-z0-9-]+/g, "-")}.png`}
                data-testid="public-qr-download"
              >
                <Download className="w-3.5 h-3.5" strokeWidth={2} /> Download PNG
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Download modal — pick scope: all vs. favourites only */}
      {showDownload && mainEvent && (
        <DownloadModal
          eventId={mainEvent.id}
          coupleName={mainEvent.couple_name || mainEvent.name}
          totalPhotos={mainEvent.photo_count || 0}
          favouriteIds={Array.from(favs.ids)}
          onClose={() => setShowDownload(false)}
        />
      )}

      {/* Mobile-only bottom action bar */}
      {mainEvent && (
        <MobileActionBar
          coupleName={mainEvent.couple_name || mainEvent.name}
          matchCount={matchState?.count || 0}
          onShare={async () => {
            const url = typeof window !== "undefined" ? window.location.href.split("?")[0] : "";
            const title = `${mainEvent.couple_name || mainEvent.name} — Kahani Gallery`;
            if (navigator.share) {
              try { await navigator.share({ title, url }); return; } catch {/* fall through */}
            }
            setShowQr(true);
          }}
          onFindMe={() => setGateOpen(true)}
          onDownload={() => setShowDownload(true)}
        />
      )}

      {/* Branded KBRJ footer */}
      {mainEvent && <KBRJFooter withMobileBarPadding />}
    </div>
  );
}

function DownloadModal({ eventId, coupleName, totalPhotos, favouriteIds, onClose }) {
  const [scope, setScope] = useState(favouriteIds.length > 0 ? "selected" : "all");
  const [started, setStarted] = useState(false);
  const canSelected = favouriteIds.length > 0;

  const startDownload = () => {
    const params = new URLSearchParams();
    if (scope === "selected") {
      params.set("scope", "selected");
      params.set("photo_ids", favouriteIds.join(","));
    } else {
      params.set("scope", "all");
    }
    const url = `${API}/public/gallery/${eventId}/download?${params.toString()}`;
    const link = document.createElement("a");
    link.href = url;
    link.rel = "noopener";
    document.body.appendChild(link);
    link.click();
    link.remove();
    setStarted(true);
    setTimeout(() => { onClose(); }, 1400);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" onClick={onClose} data-testid="public-download-modal">
      <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6 relative" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          onClick={onClose}
          className="absolute top-2 right-2 text-stone-500 hover:text-stone-900 p-1"
          data-testid="public-download-close"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
        <div className="text-[11px] uppercase tracking-[0.25em] text-rose-700 font-semibold">Download album</div>
        <h3 className="font-heading text-xl font-semibold text-stone-900 mt-1">{coupleName}</h3>
        <p className="text-xs text-stone-500 mt-1">Pick which photos to bundle into your ZIP.</p>

        <div className="mt-4 space-y-2">
          <label className={`flex items-start gap-3 p-3 border rounded-lg cursor-pointer transition-colors ${scope === "all" ? "border-rose-500 bg-rose-50" : "border-stone-200 hover:bg-stone-50"}`}>
            <input
              type="radio"
              name="download-scope"
              value="all"
              checked={scope === "all"}
              onChange={() => setScope("all")}
              className="mt-1 accent-rose-700"
              data-testid="download-scope-all"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-stone-900">All photos</div>
              <div className="text-xs text-stone-500 mt-0.5">Every image in this album ({totalPhotos})</div>
            </div>
          </label>
          <label className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${
            !canSelected ? "opacity-50 cursor-not-allowed border-stone-200" :
            scope === "selected" ? "border-rose-500 bg-rose-50 cursor-pointer" : "border-stone-200 hover:bg-stone-50 cursor-pointer"
          }`}>
            <input
              type="radio"
              name="download-scope"
              value="selected"
              checked={scope === "selected"}
              disabled={!canSelected}
              onChange={() => setScope("selected")}
              className="mt-1 accent-rose-700"
              data-testid="download-scope-selected"
            />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-semibold text-stone-900 inline-flex items-center gap-1.5">
                <Heart className="w-3.5 h-3.5 text-rose-600 fill-rose-500" strokeWidth={0} /> Only my favourites
              </div>
              <div className="text-xs text-stone-500 mt-0.5">
                {canSelected ? `${favouriteIds.length} photo${favouriteIds.length === 1 ? "" : "s"} you've hearted` : "Heart some photos first to enable this option"}
              </div>
            </div>
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="text-xs text-stone-600 hover:text-stone-900 px-3 py-2"
            data-testid="download-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={startDownload}
            disabled={started || (scope === "selected" && !canSelected)}
            className="inline-flex items-center gap-2 bg-rose-600 hover:bg-rose-700 disabled:opacity-60 text-white text-xs font-semibold px-4 py-2 rounded-full"
            data-testid="download-modal-start"
          >
            {started ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Download className="w-3.5 h-3.5" strokeWidth={2} />}
            {started ? "Starting…" : "Start download"}
          </button>
        </div>
      </div>
    </div>
  );
}

function NodeButton({ node, selected, onClick, level, chapterNumber }) {
  const isChapter = chapterNumber != null;
  return (
    <button
      type="button"
      onClick={onClick}
      className={`w-full flex items-center gap-3.5 px-2.5 py-2.5 rounded-lg text-left transition-colors ${
        selected
          ? "bg-[#3b2f24] text-[#f5efe4]"
          : "text-[#3b2f24] hover:bg-[#e7d5b4]/30"
      }`}
      data-testid={`public-node-${node.id}`}
    >
      {isChapter && (
        <span
          className={`shrink-0 ${
            selected ? "text-[#e7d5b4]" : "text-[#8c7a63]"
          }`}
          style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontStyle: "italic",
            fontSize: "18px",
            lineHeight: 1,
            minWidth: "22px",
          }}
        >
          {String(chapterNumber).padStart(2, "0")}
        </span>
      )}
      <span
        className={`flex-1 truncate ${level === "main" ? "text-[13px] font-medium" : "text-[13px]"}`}
        style={level === "main" ? { fontFamily: "'Playfair Display', Georgia, serif" } : undefined}
      >
        {node.name}
      </span>
      <span className={`text-[10px] font-mono tabular-nums shrink-0 ${
        selected ? "text-[#e7d5b4]/80" : "text-[#8c7a63]"
      }`}>
        {node.photo_count}
      </span>
    </button>
  );
}
